import { useEffect, useState } from "react";
import { faxAssignedCount } from "./faxApi";
import { eventBus } from "../utils/eventBus";

/*
 * "Assigned to me" badge on the side nav's Communication item (owner 10-13,
 * optional "only if cheap"): faxes still to work assigned to the signed-in
 * login, from v2 GET /v1/{pid}/fax/assigned-count (one indexed COUNT).
 *
 * Cheap on purpose, like the Form Inbox badge (forms/staff/useInboxCount.js):
 * asked at most once a minute per browser tab and only while the page is
 * visible; the side nav remounts between pages, so the last answer is shared
 * at module level (a mount within the minute reuses it). A 404 / 403 (a v2
 * without the route, a service login) or {available: false} (migration 009
 * not applied) hides the badge and stops asking for this page load. Assigning
 * in the Fax tab emits "fax:assigned-changed" for a fresh count.
 */
const POLL_MS = 60 * 1000;
const shared = { at: 0, value: null, disabled: false, inflight: null };

const hasStaffToken = () => {
  try {
    return Boolean(window.localStorage.getItem("auth"));
  } catch (e) {
    return false;
  }
};

function fetchCount() {
  if (shared.inflight) return shared.inflight;
  shared.inflight = faxAssignedCount()
    .then((d) => {
      if (!d || d.available !== true) {
        shared.disabled = true;
        shared.value = null;
      } else {
        shared.value = Number(d.count) || 0;
      }
      shared.at = Date.now();
      return shared.value;
    })
    .catch((e) => {
      const st = e && e.response && e.response.status;
      if (st === 404 || st === 403) {
        shared.disabled = true;
        shared.value = null;
      }
      shared.at = Date.now();
      return shared.value;
    })
    .finally(() => {
      shared.inflight = null;
    });
  return shared.inflight;
}

// Tests only.
export function resetFaxAssignedCountCache() {
  shared.at = 0;
  shared.value = null;
  shared.disabled = false;
  shared.inflight = null;
}

export default function useFaxAssignedCount(enabled) {
  const [count, setCount] = useState(() => (enabled && !shared.disabled ? shared.value : null));

  useEffect(() => {
    if (!enabled || shared.disabled) return undefined;
    let alive = true;
    const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const run = (force) => {
      if (shared.disabled || isHidden() || !hasStaffToken()) return;
      if (!force && shared.at && Date.now() - shared.at < POLL_MS) {
        setCount(shared.value);
        return;
      }
      fetchCount().then((v) => {
        if (alive) setCount(shared.disabled ? null : v);
      });
    };
    run(false);
    const timer = setInterval(() => run(true), POLL_MS);
    const onVisible = () => {
      if (!isHidden()) run(false);
    };
    const onChanged = () => run(true);
    document.addEventListener("visibilitychange", onVisible);
    eventBus.on("fax:assigned-changed", onChanged);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      eventBus.off("fax:assigned-changed", onChanged);
    };
  }, [enabled]);

  return enabled ? count : null;
}
